import isString from 'lodash/isString'
import type { PivotFilterOperatorEnum, PivotLayout, PivotLayoutFilter } from '@ztechapps/common.contract'
import { findPivotRankedFieldProblem } from '../find-pivot-ranked-field-problem'
import { isPivotNumericOperand } from '../is-pivot-numeric-operand'
import { isPivotRangeOperator } from '../is-pivot-range-operator'
import { PIVOT_VALUE_COMPARISON_OPERATORS } from '../pivot-value-comparison-operators'

/**
 * Finds the first rule a filter entry's Value comparison breaks against the layout's Rows, Columns and aggregated
 * values, or undefined when the entry holds none or it is valid. Reads untrusted model-written layouts, so every
 * level is checked before it is read. Exclusivity with the other filter kinds is the caller's rule.
 */
export const findPivotValueComparisonProblem = (
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures'>,
  filter: PivotLayoutFilter
): string | undefined => {
  const { valueComparison } = filter
  if (valueComparison === undefined) return undefined
  const name = String(filter.field)
  const { problem } = findPivotRankedFieldProblem({
    layout,
    filter,
    entry: valueComparison,
    subject: 'a Value comparison',
  })
  if (problem !== undefined) return problem
  const { operator, values } = valueComparison as Record<string, unknown>
  if (!PIVOT_VALUE_COMPARISON_OPERATORS.includes(operator as PivotFilterOperatorEnum))
    return `${name} compares with ${String(operator)}, which is not one of ${PIVOT_VALUE_COMPARISON_OPERATORS.join(', ')}.`
  const wanted = isPivotRangeOperator(operator as PivotFilterOperatorEnum) ? 2 : 1
  if (!Array.isArray(values) || values.length !== wanted)
    return `${name} compares with ${String(operator)}, which takes ${wanted === 2 ? 'two values' : 'one value'}.`
  if (!values.every((value) => isString(value) && isPivotNumericOperand(value)))
    return `${name} compares with a value that is not a number.`
  return undefined
}
